import { useMutation } from '@apollo/client';
import type { InsertInternalAuditImpactRatingMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { InsertInternalAuditImpactRatingDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import type { InsertImpactInternalAuditRatingInput } from './useInsertImpactInternalAuditRatingTRPC';
import { useInsertImpactInternalAuditRatingTRPC } from './useInsertImpactInternalAuditRatingTRPC';

export const useInsertImpactInternalAuditRating = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const [insertGraphQL, graphqlState] = useMutation(
    InsertInternalAuditImpactRatingDocument,
    {
      update: (cache) => {
        evictField(cache, 'impact_internal_audit_rating');
        evictField(cache, 'internal_audit_report');
      },
    }
  );

  const trpcMutation = useInsertImpactInternalAuditRatingTRPC();

  const insertImpactInternalAuditRating = async (
    variables: InsertImpactInternalAuditRatingInput
  ): Promise<InsertInternalAuditImpactRatingMutation> => {
    if (trpcEnabled) {
      return trpcMutation.insertImpactInternalAuditRating(variables);
    }

    const result = await insertGraphQL({ variables });
    if (!result.data) {
      throw new Error('Failed to insert impact internal audit rating');
    }

    return result.data;
  };

  if (trpcEnabled) {
    return {
      insertImpactInternalAuditRating,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    insertImpactInternalAuditRating,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
